import { useEffect, type ReactNode } from "react";
import { useWriteAccessStore } from "@/store/write-access-store";

interface Props {
  children: ReactNode;
  /**
   * Wait this long after mount before prompting, so the prompt lands on a
   * painted screen rather than on top of a loading one.
   */
  delayMs?: number;
  /** Set false to leave the asking to a feature that has a reason to ask. */
  autoRequest?: boolean;
}

const DEFAULT_DELAY = 1000;

/**
 * Makes sure the mini-app holds write access, asking once if it does not.
 *
 * Mount it near the root. It asks **on entry only** — a second after the app
 * opens, once per launch — and never again while that session lives, whatever
 * the user does in it. Navigation does not re-prompt, because the provider sits
 * above the router and its effect has no reactive dependencies.
 *
 * It is inert outside the Eitaa client, on clients too old to support the
 * request, once permission is held, and for an hour after a refusal — so in the
 * common case it does nothing at all.
 *
 * It renders its children untouched and never blocks them: write access is a
 * nice-to-have for reaching the user later, not a gate on using the app.
 */
export function WriteAccessProvider({
  children,
  delayMs = DEFAULT_DELAY,
  autoRequest = true,
}: Props) {
  useEffect(() => {
    if (!autoRequest) return;

    // Read through the store rather than a subscribed value so this runs once
    // on mount instead of re-firing every time the status changes.
    const { shouldAsk, request } = useWriteAccessStore.getState();
    if (!shouldAsk()) return;

    const timer = setTimeout(() => {
      // Checked again on fire: something else may have asked in the meantime.
      if (useWriteAccessStore.getState().shouldAsk()) void request();
    }, delayMs);

    return () => clearTimeout(timer);
  }, [autoRequest, delayMs]);

  return <>{children}</>;
}

/**
 * Write-access state plus the request action, for a feature that wants to ask
 * at a moment the user would understand — next to a "notify me" switch, say,
 * rather than on launch.
 */
export function useWriteAccess() {
  const status = useWriteAccessStore((s) => s.status);
  const requesting = useWriteAccessStore((s) => s.requesting);
  const request = useWriteAccessStore((s) => s.request);

  return {
    status,
    requesting,
    request,
    isGranted: status === "granted",
    /** The client cannot be asked at all — hide any UI that offers to. */
    isUnsupported: status === "unsupported",
  };
}

export default WriteAccessProvider;
